import { type RumGlobal } from '@datadog/browser-rum';

import { isDataDogApplicableForLevel } from '../constants';
import * as types from '../types';
import { mergeContext } from '../util';

import { Strategy } from './Strategy';

/**
 * A logger strategy, {@link types.IStrategy}, that emits the underlying log messages, context,
 * {@link types.LogContext}, and in some cases, error, {@link types.LoggerCustomError}, via the
 * {@link datadogRum.addError} method.
 *
 * This strategy will only emit log information when being used in client contexts, and is primarily
 * only applicable for NextJS applications.
 */
export class DataDogRumStrategy extends Strategy {
  private _datadogRum: null | RumGlobal | undefined = undefined;
  public strategyId: types.StrategyId = types.StrategyId.DataDogRum;

  /**
   * Logs the provided information via the underlying {@link dataDogRum} instance that the strategy
   * is configured to use.  This method should not be called directly, but should instead be called
   * via the {@link logWithStrategy} method.
   */
  protected log(
    _: types.ILoggerConfiguration,
    level: types.LogLevel,
    { context, error, message }: { readonly time?: number } & types.ParsedArgs,
  ): void {
    if (isDataDogApplicableForLevel(level)) {
      if (!this.datadogRum) {
        this.logInternally(
          types.LogLevel.Warn,
          'Attempting to log with the DataDogRumStrategy, but the DataDogRum instance is not ' +
            'available.  This is likely due to the strategy being used in a non-client context.',
        );
        return;
      }
      const datadogRumAlreadyInitialized = this.datadogRum.getInternalContext();
      if (!datadogRumAlreadyInitialized) {
        this.logInternally(
          types.LogLevel.Warn,
          'Datadog RUM is not initialized, log will not be sent to Datadog.',
        );
        return;
      }
      this.datadogRum.addError(error ?? message, mergeContext(context, { message }));
    }
  }

  /**
   * Attempts to safely import the "@datadog/browser-rum" package via a dynamic require statement,
   * logging any errors that occur during the import process.
   *
   * This method is designed to allow this strategy, {@link DataDogRumStrategy}, to be used in a
   * more forgiving manner, just in case the {@link Logger} is being used in a context where the
   * "@datadog/browser-rum" package is not available, avoiding hard errors from surfacing during
   * the issuance of logs.
   */
  private safelyImportDatadogRum() {
    let browserRumPackage: { datadogRum: RumGlobal };
    try {
      /* eslint-disable-next-line @typescript-eslint/no-require-imports,
         @typescript-eslint/no-require-imports */
      browserRumPackage = require('@datadog/browser-rum') as { datadogRum: RumGlobal };
    } catch (error) {
      this.logInternally(
        types.LogLevel.Error,
        error,
        "There was an error attempting to import the '@datadog/browser-rum' package. Datadog Rum " +
          'logging behavior will be disabled.',
      );
      return null;
    }
    return browserRumPackage.datadogRum;
  }

  /**
   * The Datadog RUM instance, {@link RumGlobal}, that is used for issuing errors and messages to
   * Datadog on the client via the {@link DataDogRumStrategy} strategy.
   */
  private get datadogRum() {
    if (typeof window === 'undefined') {
      if (this._datadogRum === undefined) {
        this._datadogRum = this.safelyImportDatadogRum();
      }
      return this._datadogRum;
    }
    return undefined;
  }

  protected get isEnabled() {
    if (typeof window === 'undefined') {
      return false;
    }
    return super.isEnabled;
  }
}
